import { FC, useCallback, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form, Passwordbox, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Modal, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph } from "capitalrx-components-2.0/lib/typography";

import { challengeLogin, cognitoLogin, migrateNotification } from "@/App/api/login";
import { ThemedButton, ThemedSubmitButton } from "@/App/components";
import { ClientAwareLink } from "@/App/components/ClientAwareLink";
import { AuthSources, StorageKeys } from "@/App/constants/constants";
import { AuthenticationContext, ExperienceContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { enableMultifactorAuthentication } from "@/App/utils/featureFlags";

interface LoginFormData {
  readonly email: string;
  readonly password: string;
}


export const LoginPage: FC = () => {
  const [loginMethod, setLoginMethod] = useState<string | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [modalInfo, setModalInfo] = useState<any>(undefined);

  const { hideMemberRegistration } = useContext(ExperienceContext);
  const { setAuthState, getAuthToken } = useContext(AuthenticationContext);

  const { t: translate } = useTranslation();

  const navigate = useNavigateClient();

  const form = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
      tablet: false,
    },
  });

  const alert = useAlert();

  const SP_DISABLED_FALLBACK = translate("LOGIN_PAGE.spDisabledFallback");
  const MIGRATION_MSG_FALLBACK = translate("LOGIN_PAGE.migrationMessageFallback");

  const checkLogin = async (formData: LoginFormData) => {
    try {
      const challengeLoginResponse = await challengeLogin({
        identity: formData.email,
      });
      const { authenticator, redirect } = challengeLoginResponse;
      localStorage.setItem(StorageKeys.AUTH_SOURCE, authenticator);
      if (authenticator === AuthSources.AWS_COGNITO) {
        logEvent(ANALYTIC_EVENTS.LOGIN.challenge);
        setLoginMethod(authenticator);
      } else {
        window.location.href = redirect;
      }
    } catch (error) {
      if (error?.status === 406) {
        setModalInfo(error.data);
      } else {
        alert({
          message: error?.data?.message || translate("LOGIN_PAGE.userCannotAuthenticate"),
          type: "error",
          style: "toast",
        });
      }
    }
  };

  const loginUser = async (formData: LoginFormData) => {
    try {
      const response = await cognitoLogin({
        identity: formData.email,
        password: formData.password,
      });
      if (!enableMultifactorAuthentication()) {
        logEvent(ANALYTIC_EVENTS.LOGIN.success);
        setAuthState("signedIn");
        form.reset();
      } else {
        const nextStep = response.nextStep?.signInStep;
        if (nextStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
          navigate("/login-totp-mfa");
          form.reset();
        } else if (nextStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
          navigate("/login-sms-mfa");
          form.reset();
        } else if (nextStep === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION") {
          navigate("/login-select-mfa");
          form.reset();
        } else if (nextStep === "CONFIRM_SIGN_UP") {
          const confirmParams = new URLSearchParams();
          confirmParams.set("email", formData.email);
          confirmParams.set("resend", "true");
          navigate(`/confirm?${confirmParams}`);
          form.reset();
        } else if (response.isSignedIn) {
          logEvent(ANALYTIC_EVENTS.LOGIN.success);
          setAuthState("signedIn");
          form.reset();
        } else {
          let message = "User cannot authenticate.";
          // present some possible solutions to sign-in issues to the member to assist with member support
          if (nextStep === "RESET_PASSWORD") {
            message += " " + translate("LOGIN_PAGE.resetPassword");
          }
          alert({
            message,
            type: "error",
            style: "toast",
          });
        }
      }
    } catch (error) {
      let message = "Unable to login.";
      // allow some select messages from cognito to be presented to the member to assist with member support
      if (error.name == "NotAuthorizedException") {
        if (error.message == translate("LOGIN_PAGE.notAuthorizedException")) {
          message += " " + error.message;
        } else {
          message += " Not authorized.";
        }
      } else if (error.name == "PasswordResetRequiredException") {
        message += " " + translate("LOGIN_PAGE.passwordResetRequiredException");
      }
      setAuthState("signedOut");
      logEvent(ANALYTIC_EVENTS.LOGIN.failure);
      alert({
        message,
        type: "error",
        style: "toast",
      });
    }
  };

  const submitForm = useCallback(
    async (formData: LoginFormData) => {
      if (loginMethod === undefined) {
        await checkLogin(formData);
      } else {
        await loginUser(formData);
      }
    },
    [loginMethod, checkLogin, loginUser, setAuthState, getAuthToken],
  );

  const modalDismissed = async () => {
    const destination = modalInfo.disable_sp_url;

    if (modalInfo.notified_of_migration === false) {
      try {
        await migrateNotification(form.watch().email);
        logEvent(ANALYTIC_EVENTS.LOGIN.migrationNotification);
      } catch {
        logEvent(ANALYTIC_EVENTS.ERRORS.migrationNotification);
      }
    }

    window.location.href = destination;
  };

  const modalClosed = () => {
    setModalInfo(undefined);
  };

  return (
    <Form form={form} onSubmit={submitForm} testId="login">
      <Modal
        testId="sso-notice"
        title={translate("LOGIN_PAGE.ssoNotice")}
        onClose={modalClosed}
        visible={!!modalInfo}
        footer={
          <Shelf spacing="none">
            <Shelf.Spacer />
            <ThemedButton testId="sso-notice-continue" appearance="primary" onClick={modalDismissed}>
              {translate("LOGIN_PAGE.continueToMemberPortal")}
            </ThemedButton>
          </Shelf>
        }
      >
        <Paragraph testId="sso-notice-message">
          {modalInfo?.notified_of_migration === false && (modalInfo?.migration_message || MIGRATION_MSG_FALLBACK)}
          {modalInfo?.notified_of_migration !== false && (modalInfo?.disable_sp_message || SP_DISABLED_FALLBACK)}
        </Paragraph>
      </Modal>
      <Grid>
        <Grid.Span columns={12}>
          <Textbox.Field testId="email" name="email" label={translate("LOGIN_PAGE.emailAddress")} placeholder={translate("LOGIN_PAGE.emailAddress")} required readOnly={!!loginMethod} />
        </Grid.Span>
        {loginMethod === undefined && (
          <Grid.Span columns={12}>
            <ThemedSubmitButton testId="next" appearance="primary" icon="arrow-right" iconPosition="right">
              {translate("LOGIN_PAGE.next")}
            </ThemedSubmitButton>
          </Grid.Span>
        )}
        {loginMethod === AuthSources.AWS_COGNITO && (
          <Grid.Span columns={12}>
            <Passwordbox.Field testId="password" enforceStrength={false} name="password" placeholder={translate("LOGIN_PAGE.password")} label={translate("LOGIN_PAGE.password")} required />
          </Grid.Span>
        )}
        {loginMethod === AuthSources.AWS_COGNITO && (
          <Grid.Span columns={12}>
            <ThemedSubmitButton testId="login" appearance="primary" iconPosition="right">
              {translate("LOGIN_PAGE.loginCTA")}
            </ThemedSubmitButton>
          </Grid.Span>
        )}
        {isCompressed && (
          <Stack>
            <Shelf>
              <Shelf.Spacer />
              <Body>{translate("LOGIN_PAGE.dontHaveAnAccount")}</Body>
              <ClientAwareLink testId="go-to-register" to="/register">
                {translate("LOGIN_PAGE.registerNow")}
              </ClientAwareLink>
              <Shelf.Spacer />
            </Shelf>
            <Shelf>
              <Shelf.Spacer />
              <ClientAwareLink testId="go-to-forgot-password" to="/forgot-password">
                {translate("LOGIN_PAGE.forgotPassword")}
              </ClientAwareLink>
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        )}
        {!isCompressed && (
          <Shelf>
            {hideMemberRegistration && <Shelf.Spacer />}
            <ClientAwareLink testId="go-to-forgot-password" to="/forgot-password">
              {translate("LOGIN_PAGE.forgotPassword")}
            </ClientAwareLink>
            <Shelf.Spacer />
            {!hideMemberRegistration && (
              <Shelf spacing="mini">
                <Body>{translate("LOGIN_PAGE.dontHaveAnAccount")}</Body>
                <ClientAwareLink testId="go-to-register" to="/register">
                  {translate("LOGIN_PAGE.registerNow")}
                </ClientAwareLink>
              </Shelf>
            )}
          </Shelf>
        )}
      </Grid>
    </Form>
  );
};
